import Decimal from 'decimal.js-light';
import { useMemo, useState } from 'react';
import { formatUnits } from 'viem';

import { tokenFormatter } from '@/lib/formatting';

import { useTradeEngine } from '@/components/TransactionModule/useTradeEngine';

import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

type NetworkCostAmountProps = {
  chainId: number;
  totalNetworkFee: string | undefined;
};

export const NetworkCostAmount = ({ chainId, totalNetworkFee }: NetworkCostAmountProps) => {
  const [showFiat, setShowFiat] = useState(false);
  const {
    tokenUsdPrices: { nativeTokenUsd },
  } = useTradeEngine();
  const { currencyCode, formatFiat } = useFiatDisplay();
  const nativeCurrency = SUPPORTED_CHAINS_BY_CHAIN_ID[chainId]?.nativeCurrency;

  const nativeAmount = useMemo(() => {
    if (!totalNetworkFee || !nativeCurrency) return undefined;
    return new Decimal(formatUnits(BigInt(totalNetworkFee), nativeCurrency.decimals));
  }, [nativeCurrency, totalNetworkFee]);

  if (!nativeAmount || !nativeCurrency) return <span>Estimate</span>;

  const tokenAmount = tokenFormatter.format(nativeAmount, { symbol: nativeCurrency.symbol });
  const fiatAmount = formatFiat(nativeAmount.mul(nativeTokenUsd));
  const nextUnit = showFiat ? nativeCurrency.symbol : currencyCode;

  return (
    <button
      type="button"
      className="text-token-gray-1600 bg-transparent border-none p-0 cursor-pointer hover:underline"
      aria-label={`Show network cost in ${nextUnit}`}
      onClick={() => setShowFiat((current) => !current)}
    >
      {showFiat ? fiatAmount : tokenAmount}
    </button>
  );
};
